import React, { useEffect, useState, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { createExpense, updateExpense } from "../api/expenses"
import { getRequest } from "@react/helpers/fetch"
import { pathname } from "@helpers/javascript/javascript"
import { COST_CATEGORIES, CATEGORY_COLORS } from "../../../quotes/form/qlines/shared/category_styles"

const toEuros = cents => (cents == null ? "" : (cents / 100).toString().replace(".", ","))

const todayISO = () => new Date().toISOString().slice(0, 10)

const ExpenseModal = ({
  isOpen, mode, projectId, expense,
  enabledCategories, analyticAdvanced, analyticLookups,
  projectDefaults, // { departmentId, tradeId } — pre-fills the allocation
  onClose, onSaved,
}) => {
  const [description, setDescription] = useState("")
  const [issueDate,   setIssueDate]   = useState(todayISO())
  const [amount,      setAmount]      = useState("")
  const [category,    setCategory]    = useState("other")
  const [saving,      setSaving]      = useState(false)
  const [errors,      setErrors]      = useState([])

  // Analytic segments (Lot 2.5) — shown only when the account
  // is in advanced profile. The department/trade lists are inlined
  // from the panorama payload; the CR elements are fetched on the fly
  // via /projects/analytics/cost_revenue_elements.json (contextual filter EX-B02).
  const [crElementId,   setCrElementId]   = useState(null)
  const [crElementName, setCrElementName] = useState("")
  const [departmentId,  setDepartmentId]  = useState(null)
  const [tradeId,       setTradeId]       = useState(null)
  const [avenantId,     setAvenantId]     = useState(null)
  const [crSuggestions, setCrSuggestions] = useState([])
  const [crSearchQ,     setCrSearchQ]     = useState("")
  const crSearchTimer = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    setErrors([])
    if (mode === "edit" && expense) {
      setDescription(expense.description || "")
      setIssueDate(expense.issue_date || todayISO())
      setAmount(toEuros(expense.unit_price?.cents))
      setCategory(expense.cost_category || "other")
      setCrElementId(expense.cost_revenue_element_id || null)
      setCrElementName(expense.cost_revenue_element ? `${expense.cost_revenue_element.code} — ${expense.cost_revenue_element.name}` : "")
      setDepartmentId(expense.department_id || null)
      setTradeId(expense.trade_id || null)
      setAvenantId(expense.avenant_id || null)
    } else {
      setDescription("")
      setIssueDate(todayISO())
      setAmount("")
      setCategory("other")
      setCrElementId(null)
      setCrElementName("")
      // Pre-fill with the worksite's department/trade (inheritance).
      // The user can still change it on the line.
      setDepartmentId(projectDefaults?.departmentId || null)
      setTradeId(projectDefaults?.tradeId || null)
      // No amendment pre-fill: by default the expense
      // belongs to the base contract.
      setAvenantId(null)
    }
    setCrSuggestions([])
    setCrSearchQ("")
  }, [isOpen, mode, expense])

  // Contextual fetch of CR elements (debounced 250ms).
  useEffect(() => {
    if (!analyticAdvanced) return
    if (crSearchTimer.current) clearTimeout(crSearchTimer.current)
    crSearchTimer.current = setTimeout(() => {
      const params = { project_id: projectId, cost_category: category, q: crSearchQ }
      if (tradeId) params.trade_id = tradeId
      getRequest(pathname({ path: "/projects/analytics/cost_revenue_elements", format: "json", params }))
        .then(setCrSuggestions)
        .catch(() => setCrSuggestions([]))
    }, 250)
    return () => crSearchTimer.current && clearTimeout(crSearchTimer.current)
  }, [analyticAdvanced, projectId, category, tradeId, crSearchQ])

  const handleSave = () => {
    if (saving) return
    if (!description) {
      setErrors([I18n.t("projects.expenses.errors.description_required")])
      return
    }
    if (!amount) {
      setErrors([I18n.t("projects.expenses.errors.amount_required")])
      return
    }
    setSaving(true)
    setErrors([])

    const body = {
      description,
      issue_date:        issueDate,
      unit_price_amount: amount,
      cost_category:     category,
      quantity:          1,
    }
    if (analyticAdvanced) {
      body.cost_revenue_element_id = crElementId
      body.department_id           = departmentId
      body.trade_id                = tradeId
      body.avenant_id              = avenantId
    }

    const request = (mode === "edit" && expense)
      ? updateExpense(projectId, expense.id, body)
      : createExpense(projectId, body)

    request
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        onSaved && onSaved(response)
        onClose()
      })
      .catch(() => setErrors([I18n.t("projects.expenses.errors.network")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.expenses.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {mode === "edit"
            ? I18n.t("projects.expenses.form.save")
            : I18n.t("projects.expenses.form.create")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit"
        ? I18n.t("projects.expenses.form.eyebrow_edit")
        : I18n.t("projects.expenses.form.eyebrow_new")}
      title   = {mode === "edit"
        ? (expense?.description || I18n.t("projects.expenses.form.title_fallback"))
        : I18n.t("projects.expenses.form.title_new")}
      footer  = {footer}
    >
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.expenses.form.description")} <span className="text-rose-500">*</span>
          </label>
          <input
            value     = {description}
            onChange  = {e => setDescription(e.target.value)}
            placeholder = {I18n.t("projects.expenses.form.description_placeholder")}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.expenses.form.issue_date")}
          </label>
          <input
            type      = "date"
            value     = {issueDate}
            onChange  = {e => setIssueDate(e.target.value)}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.expenses.form.amount")} <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <input
              value     = {amount}
              onChange  = {e => setAmount(e.target.value)}
              placeholder = "0,00"
              className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
            />
            <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
          </div>
        </div>

        <div className="col-span-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.expenses.form.category")}
          </label>
          <select
            value     = {category}
            onChange  = {e => setCategory(e.target.value)}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            style     = {{ borderLeft: `3px solid ${CATEGORY_COLORS[category] || "#6b7280"}` }}
          >
            {(() => {
              const base    = (Array.isArray(enabledCategories) && enabledCategories.length > 0) ? enabledCategories : COST_CATEGORIES
              const visible = base.includes(category) || !COST_CATEGORIES.includes(category) ? base : [...base, category]
              return visible.map(cat => (
                <option key={cat} value={cat}>
                  {I18n.t(`quotes.new.cost_category.${cat}`)}
                </option>
              ))
            })()}
          </select>
        </div>
      </div>

      {analyticAdvanced && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {I18n.t("projects.expenses.form.analytic.title")}
          </div>
          <div className="grid grid-cols-12 gap-4">
            {/* CR element — combobox with debounced fetch */}
            <div className="col-span-12">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {I18n.t("projects.expenses.form.analytic.cr_element")}
              </label>
              <input
                type        = "text"
                value       = {crSearchQ || crElementName}
                onChange    = {e => { setCrSearchQ(e.target.value); setCrElementId(null); setCrElementName("") }}
                placeholder = {I18n.t("projects.expenses.form.analytic.cr_element_placeholder")}
                className   = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
              />
              {crSearchQ && crSuggestions.length > 0 && !crElementId && (
                <div className="mt-1 border border-slate-200 rounded-md max-h-48 overflow-auto bg-white shadow-sm">
                  {crSuggestions.map(s => (
                    <div
                      key       = {s.id}
                      onClick   = {() => {
                        setCrElementId(s.id)
                        setCrElementName(`${s.code} — ${s.name}`)
                        setCrSearchQ("")
                      }}
                      className = "px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0"
                    >
                      <span className="font-mono text-[12px] text-slate-500 mr-2">{s.code}</span>
                      {s.name}
                      <span className="ml-2 text-[11px] text-slate-400">{s.node_code} · {s.level}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trade */}
            <div className="col-span-6">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{I18n.t("projects.expenses.form.analytic.trade")}</label>
              <select
                value     = {tradeId || ""}
                onChange  = {e => setTradeId(e.target.value ? parseInt(e.target.value) : null)}
                className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
              >
                <option value="">—</option>
                {(analyticLookups?.trades || []).map(t =>
                  <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
                )}
              </select>
            </div>

            {/* Department */}
            <div className="col-span-6">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{I18n.t("projects.expenses.form.analytic.department")}</label>
              <select
                value     = {departmentId || ""}
                onChange  = {e => setDepartmentId(e.target.value ? parseInt(e.target.value) : null)}
                className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
              >
                <option value="">—</option>
                {(analyticLookups?.departments || []).map(d =>
                  <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
                )}
              </select>
            </div>

            {/* Avenant / TS (FB BALAS #4) — visible only if there is at least one signed amendment */}
            {(analyticLookups?.avenants || []).length > 0 && (
              <div className="col-span-12">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  {I18n.t("projects.expenses.form.analytic.avenant")}
                  <span className="text-slate-400 ml-1">{I18n.t("projects.expenses.form.analytic.avenant_hint")}</span>
                </label>
                <select
                  value     = {avenantId || ""}
                  onChange  = {e => setAvenantId(e.target.value ? parseInt(e.target.value) : null)}
                  className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
                >
                  <option value="">{I18n.t("projects.expenses.form.analytic.base_contract")}</option>
                  {(analyticLookups?.avenants || []).map(a =>
                    <option key={a.id} value={a.id}>{a.number} — {a.title}</option>
                  )}
                </select>
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            {I18n.t("projects.expenses.form.analytic.prefill_hint")}
          </p>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default ExpenseModal
